import currentUserQuery from "../queries/CurrentUser.graphql";
// cache-6
import React, { useEffect, useState, useCallback } from "react";
import {
  AppBar,
  Dialog,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Slide,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useAuth0 } from "@auth0/auth0-react";
import useReliableAuth from "../hooks/useReliableAuth";
import { HashLink } from "react-router-hash-link";
import { ArrowDropDown, Close, Menu } from "@material-ui/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import classnames from "classnames";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useQuery } from "@apollo/client";

import AccountMenuBtn from "./AccountMenuBtn";
import { constants } from "../global";
import LogoFull from "./LogoFull";
import FaviconLogo from "./FaviconLogo";
import CustomButton from "./CustomButton";
import SquircleBox from "./SquircleBox";
import createAsyncComponent from "../utils/createAsyncComponent";
import {
  CONTENT_FRAME_STYLE,
  CONTENT_GRID_PROPS,
  CONTENT_INNER_STYLE,
  CONTENT_SIDE_PADDING,
} from "../layout";


const TermsOfAdhesionSigningDialog = createAsyncComponent(
  () => import("./TermsOfAdhesionSigningDialog"),
  {
    displayName: "AsyncTermsOfAdhesionSigningDialog",
  },
);
const PartnershipAgreementSigningDialog = createAsyncComponent(
  () => import("./PartnershipAgreementSigningDialog"),
  {
    displayName: "AsyncPartnershipAgreementSigningDialog",
  },
);

const useStyles = makeStyles((theme) => ({
  appBar: {
    width: "100%",
    boxShadow: "none",
    background: "transparent",
    transition: "all 0.25s ease",
  },
  toolbar: {
    minHeight: "82px",
    padding: "0 !important",
  },
  shellWrap: {
    margin: "0.4rem 0 0.8rem",
    borderRadius: "24px",
    transition: "box-shadow 0.25s ease",
  },
  headerFrame: {
    ...CONTENT_FRAME_STYLE,
    ...CONTENT_INNER_STYLE,
    boxSizing: "border-box",
  },
  shellWrapScrolled: {
    boxShadow: "0 10px 28px rgba(14, 15, 12, 0.22)",
  },
  desktopShell: {
    width: "100%",
    margin: "0 auto",
    borderRadius: "24px",
    border: "1px solid rgba(255, 255, 255, 0)",
    background: "transparent",
    padding: "10px 14px",
    transition: "all 0.3s ease",
  },
  desktopShellScrolled: {
    background: "rgba(1, 54, 46, 0.78)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
  },
  desktopNavBtn: {
    color: "#fefefe",
    minHeight: "44px",
    padding: "0 16px",
    borderRadius: "14px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    letterSpacing: ".01em",
    "@media (min-width: 960px) and (max-width: 1494px)": {
      minHeight: "40px",
      padding: "0 10px",
      fontSize: "0.84rem",
    },
    "&:hover": {
      color: "#fefefe",
      background: "rgba(255,255,255,0.16)",
    },
  },
  glassButton: {
    margin: "0 3px",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    transition:
      "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
    border: "none",
    "&.MuiButton-root": {
      transition:
        "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
    },
    "@media (min-width: 960px) and (max-width: 1494px)": {
      margin: "0 1px",
    },
  },
  glassButtonTop: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    isolation: "isolate",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.24)",
    },
  },
  navBtn: {
    minHeight: "44px",
    padding: "0 20px",
    borderRadius: "14px",
    fontWeight: 800,
    whiteSpace: "nowrap",
    boxShadow: "none",
    "@media (min-width: 960px) and (max-width: 1494px)": {
      minHeight: "40px",
      padding: "0 14px",
      fontSize: "0.86rem",
    },
    "&:hover": {
      boxShadow: "none",
      backgroundColor: theme.palette.secondary.light,
    },
  },
  socialLinks: {
    color: "rgba(255, 255, 255, 0.8)",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.secondary.main,
    },
  },
  mobileMenuSurface: {
    height: "100%",
    background:
      "radial-gradient(circle at 15% 10%, rgba(149,227,98,0.2), transparent 40%), linear-gradient(140deg, #01362E 0%, #012922 100%)",
  },
  mobileLink: {
    borderRadius: "14px",
    padding: "12px 18px",
    marginBottom: "0.35rem",
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
    },
  },
  mobileSelected: {
    background: "rgba(255, 255, 255, 0.12)",
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const Header = (props) => {
  const { setContactFormOpen, setSavingsCalculatorOpen, opaque, data } = props;
  const theme = useTheme();
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery("(min-width: 960px) and (max-width: 1494px)");

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subLinksAnchorEl, setSubLinksAnchorEl] = useState(null);
  const [subLinks, setSubLinks] = useState(null);
  const [scrollTop, setScrollTop] = useState(true);
  const forceOpaque = opaque && location.pathname !== "/";
  const elevatedShell = !scrollTop || forceOpaque;
  const useTopGlassButtons = scrollTop && !forceOpaque;
  const headerTopOffset = 0;
  const mobileUseLightSurface = !scrollTop || forceOpaque;

  const { loginWithRedirect } = useAuth0();
  const { isAuthenticated, logout } = useReliableAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrollTop(window.scrollY < 12);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleOpenSubLinks = useCallback(
    (nextSubLinks) => (event) => {
      setSubLinks(nextSubLinks);
      setSubLinksAnchorEl(event.currentTarget);
    },
    []
  );

  const getLinks = () => [
    {
      label: "Como funciona",
      slug: "#como-funciona",
      desktopPosition: "left",
    },
    {
      label: "Quem somos",
      slug: "#quem-somos",
      desktopPosition: "left",
      hideMobile: true,
    },
    {
      label: "Projetos",
      slug: "#projetos",
      desktopPosition: "left",
    },
    {
      label: "Parceiros",
      slug: "parceiros",
      desktopPosition: "left",
    },
    {
      label: "Contato",
      onClick: () => {
        setContactFormOpen(true);
        setMobileMenuOpen(false);
      },
      desktopPosition: "left",
    },
    {
      label: "Simular Economia",
      onClick: () => {
        setSavingsCalculatorOpen(true);
        setMobileMenuOpen(false);
      },
      desktopPosition: "left",
    },
    isAuthenticated && {
      label: "Meu Painel",
      hideDesktop: true,
      onClick: () => {
        navigate("/dashboard");
      },
    },
    isAuthenticated && {
      label: "Sair",
      hideDesktop: true,
      onClick: () => {
        logout();
      },
    },
    !isAuthenticated && {
      label: "Quero Economizar",
      desktopPosition: "right",
      slug: "register",
      pill: true,
    },
    !isAuthenticated && {
      label: "Entrar",
      onClick: () => {
        sessionStorage.setItem("auth0_login_redirect", "true");
        sessionStorage.setItem("auth0_login_time", Date.now().toString());
        loginWithRedirect({
          authorizationParams: {
            screen_hint: "login",
            scope: "openid profile email",
            ui_locales: "pt-BR",
          },
          appState: {
            returnTo: "/dashboard?welcome=1",
          },
        });
      },
      desktopPosition: "right",
    },
  ];

  const renderSubLinks = () => {
    if (!subLinks) return null;

    return (
      <List>
        {subLinks.map((link) => (
          <ListItem
            key={`header-item-${link.slug}`}
            button
            smooth
            component={HashLink}
            to={`/${link.slug}`}
            style={{ color: theme.palette.primary.main }}
          >
            <ListItemAvatar
              style={{ textAlign: "center", minWidth: "48px", marginRight: "8px" }}
            >
              <i style={{ fontSize: "1.1rem" }} className={link.iconClass} />
            </ListItemAvatar>
            <ListItemText primary={<b>{link.label}</b>} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderDesktopButton = (link, index, rightSide = false) => {
    let component;
    let onClick;
    const isPill = !!link.pill;

    if (link.onClick && !link.slug) {
      component = undefined;
      onClick = link.onClick;
    } else if (link.subLinks) {
      component = undefined;
      onClick = handleOpenSubLinks(link.subLinks);
    } else if (link.slug) {
      component = link.slug.startsWith("#") ? HashLink : Link;
      onClick = undefined;
    }
    const useSmoothScroll = component === HashLink;

    return (
      <CustomButton
        key={`menu-item-${rightSide ? "right" : "left"}-${index}`}
        className={classnames(
          isPill ? classes.navBtn : classes.desktopNavBtn,
          !isPill ? classes.glassButton : null,
          !isPill && useTopGlassButtons ? classes.glassButtonTop : null
        )}
        onClick={onClick}
        variant={isPill ? "contained" : "text"}
        color={isPill ? "secondary" : "default"}
        component={component}
        smooth={useSmoothScroll ? true : undefined}
        to={!link.subLinks && !link.onClick ? `/${link.slug}` : undefined}
        style={{
          marginLeft: rightSide ? "0.35rem" : 0,
          marginRight: !rightSide ? "0.35rem" : 0,
        }}
      >
        <span>
          {link.label}
          {link.subLinks ? (
            <ArrowDropDown style={{ position: "absolute", right: "2px" }} />
          ) : null}
        </span>
      </CustomButton>
    );
  };

  const renderDesktopLinks = () => (
    <Grid container alignItems="center" justifyContent="center">
      <Grid
        item
        {...CONTENT_GRID_PROPS}
        className={classes.headerFrame}
      >
        <div
          className={classnames(classes.shellWrap, {
            [classes.shellWrapScrolled]: elevatedShell,
          })}
        >
          <SquircleBox
            cornerRadius={24}
            cornerSmoothing={0.82}
            backgroundColor={elevatedShell ? "rgba(1, 54, 46, 0.78)" : "transparent"}
            borderColor={
              elevatedShell ? "rgba(255, 255, 255, 0.16)" : "rgba(255, 255, 255, 0)"
            }
            borderWidth={1}
            style={
              elevatedShell
                ? {
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                  }
                : undefined
            }
            innerStyle={{ padding: "10px 14px" }}
          >
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              wrap="nowrap"
            >
            <Grid item>
              <Grid container alignItems="center" wrap="nowrap">
                <Grid item>
                  <Link to="/" className="no-select">
                    {mediumScreen ? (
                      <FaviconLogo
                        style={{
                          marginRight: "0.8rem",
                          marginTop: "-2px",
                          verticalAlign: "middle",
                          height: "30px",
                        }}
                        darkMode
                      />
                    ) : (
                      <LogoFull
                        style={{
                          marginRight: "1rem",
                          marginTop: "-2px",
                          verticalAlign: "middle",
                        }}
                        width={constants.headerLogoWidth}
                        darkMode
                      />
                    )}
                  </Link>
                </Grid>
                <Grid item>
                  {getLinks()
                    .filter(
                      (el) =>
                        el && el.desktopPosition === "left" && !el.hideDesktop
                    )
                    .map((link, index) => renderDesktopButton(link, index))}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems="center" wrap="nowrap">
                {getLinks()
                  .filter(
                    (el) =>
                      el && el.desktopPosition === "right" && !el.hideDesktop
                  )
                  .map((link, index) => renderDesktopButton(link, index, true))}
                {isAuthenticated ? <AccountMenuBtn /> : null}
              </Grid>
            </Grid>
            </Grid>
          </SquircleBox>
        </div>
      </Grid>
      <Popover
        onClose={() => setSubLinksAnchorEl(null)}
        anchorEl={subLinksAnchorEl}
        open={!!subLinksAnchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        style={{ marginTop: "1rem" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {renderSubLinks()}
      </Popover>
    </Grid>
  );

  const renderMobileLinks = () => {
    const curPath = location.pathname;
    const mobileScrolledInset = "0.6rem";
    const mobileDefaultHorizontalPadding = CONTENT_SIDE_PADDING;
    const mobileScrolledHorizontalPadding = `calc(${CONTENT_SIDE_PADDING} - ${mobileScrolledInset})`;

    return (
      <Grid
        container
        style={{
          width: mobileUseLightSurface
            ? `calc(100% - (${mobileScrolledInset} * 2))`
            : "100%",
          paddingTop: "0.6rem",
          paddingBottom: "0.6rem",
          paddingLeft: mobileUseLightSurface
            ? mobileScrolledHorizontalPadding
            : mobileDefaultHorizontalPadding,
          paddingRight: mobileUseLightSurface
            ? mobileScrolledHorizontalPadding
            : mobileDefaultHorizontalPadding,
          marginLeft: mobileUseLightSurface ? mobileScrolledInset : 0,
          marginRight: mobileUseLightSurface ? mobileScrolledInset : 0,
          borderRadius: "24px",
          backgroundColor: mobileUseLightSurface
            ? "rgba(255, 255, 255, 0.88)"
            : "transparent",
          border: mobileUseLightSurface
            ? "1px solid rgba(1, 54, 46, 0.12)"
            : "1px solid transparent",
          boxShadow: mobileUseLightSurface
            ? "0 8px 24px rgba(14, 15, 12, 0.14)"
            : "none",
          backdropFilter: mobileUseLightSurface ? "blur(8px)" : "none",
          WebkitBackdropFilter: mobileUseLightSurface ? "blur(8px)" : "none",
          transition: "all 0.25s ease",
        }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Link to="/" className="no-select">
            <LogoFull
              style={{ marginRight: "1rem", verticalAlign: "middle" }}
              width={constants.headerLogoWidth}
              darkMode={!mobileUseLightSurface}
            />
          </Link>
        </Grid>
        <Grid item>
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            color="inherit"
            aria-label="menu"
            style={{
              color: mobileUseLightSurface
                ? theme.palette.primary.main
                : theme.palette.white.main,
            }}
          >
            <Menu />
          </IconButton>
        </Grid>
        <Dialog
          onClose={() => setMobileMenuOpen(false)}
          open={!!mobileMenuOpen}
          fullScreen
          TransitionComponent={Transition}
        >
          <IconButton
            size="large"
            style={{
              color: theme.palette.white.main,
              position: "absolute",
              margin: "16px 20px",
              top: 0,
              right: 0,
              zIndex: 2,
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <Close />
          </IconButton>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            direction="column"
            className={classes.mobileMenuSurface}
          >
            {getLinks()
              .filter((el) => el && !el.hideMobile)
              .map((link, index) => {
                let component;
                let to;
                let onClick;

                if (link.onClick) {
                  component = undefined;
                  onClick = link.onClick;
                } else if (link.subLinks) {
                  component = undefined;
                  onClick = handleOpenSubLinks(link.subLinks);
                } else {
                  const isHash = link.slug?.startsWith("#");
                  component = isHash ? HashLink : Link;
                  to = `/${link.slug}`;
                  onClick = () => setMobileMenuOpen(false);
                }

                if (fullScreen && link.subLinks) {
                  return link.subLinks.map((subLink) => (
                    <Grid item key={`menu-item-sub-${subLink.slug}-${index}`}>
                      <CustomButton
                        variant="text"
                        size="large"
                        className={classnames(classes.mobileLink, {
                          [classes.mobileSelected]: curPath === `/${subLink.slug}`,
                        })}
                        component={Link}
                        onClick={() => setMobileMenuOpen(false)}
                        to={subLink.slug}
                      >
                        <b
                          style={{
                            color: theme.palette.white.main,
                            fontSize: "24px",
                          }}
                        >
                          {subLink.label}
                        </b>
                      </CustomButton>
                    </Grid>
                  ));
                }

                return (
                  <Grid item key={`menu-item-link-${index}`}>
                    <CustomButton
                      variant="text"
                      size="large"
                      className={classnames(classes.mobileLink, {
                        [classes.mobileSelected]: curPath === `/${link.slug}`,
                      })}
                      component={component}
                      onClick={onClick}
                      smooth={component === HashLink ? true : undefined}
                      to={to}
                    >
                      <b
                        style={{
                          color: theme.palette.white.main,
                          fontSize: "24px",
                        }}
                      >
                        {link.label}
                      </b>
                    </CustomButton>
                  </Grid>
                );
              })}
            <Grid item style={{ padding: "34px 0 24px" }}>
              <Typography variant="h4">
                <Grid container spacing={4}>
                  <Grid item>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram Profile"
                      href={constants.instagramUrl}
                    >
                      <i
                        className={classnames(["fab fa-instagram", classes.socialLinks])}
                      />
                    </a>
                  </Grid>
                  <Grid item>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Linked In Profile"
                      href={constants.linkedInUrl}
                    >
                      <i
                        className={classnames(["fab fa-linkedin", classes.socialLinks])}
                      />
                    </a>
                  </Grid>
                  <Grid item>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="WhatsApp"
                      href={constants.whatsAppUrl}
                    >
                      <i
                        className={classnames(["fab fa-whatsapp", classes.socialLinks])}
                      />
                    </a>
                  </Grid>
                </Grid>
              </Typography>
            </Grid>
          </Grid>
        </Dialog>
      </Grid>
    );
  };

  return (
    <>
      <AppBar
        className={classes.appBar}
        elevation={0}
        style={{
          top: headerTopOffset,
          backgroundColor: "transparent",
        }}
      >
        <Toolbar className={classes.toolbar}>
          {fullScreen ? renderMobileLinks() : renderDesktopLinks()}
        </Toolbar>
      </AppBar>
      {data &&
        !data.loading &&
        !data.error &&
        data.me?.brContact &&
        data.me.brContact?.pendingTermsOfAdhesion?.pendingSignatureViewUrl && (
          <TermsOfAdhesionSigningDialog
            open={true}
            pdfUrl={
              data.me.brContact.pendingTermsOfAdhesion.pendingSignatureViewUrl
            }
            downloadUrl={
              data.me.brContact.pendingTermsOfAdhesion
                .pendingSignatureDownloadUrl
            }
            brPowerPlan={data.me.brContact.pendingTermsOfAdhesion?.brPowerPlan}
            brConsumerUnit={
              data.me.brContact.pendingTermsOfAdhesion?.brConsumerUnit
            }
            signersName={
              data.me.brContact.pendingTermsOfAdhesion?.brConsumerUnit
                ?.brCustomer?.primaryBrContact?.fullName
            }
            termsOfAdhesionId={data.me.brContact.pendingTermsOfAdhesion.id}
          />
        )}
      {data &&
        !data.loading &&
        !data.error &&
        data.me?.brContact &&
        data.me.brContact?.brSalesPerson?.pendingAgreementDownloadUrl &&
        data.me.brContact.brSalesPerson.adminBrContact?.id ===
          data.me.brContact.id && (
          <PartnershipAgreementSigningDialog
            open={true}
            pdfUrl={data.me.brContact.brSalesPerson.pendingAgreementDownloadUrl}
            signersName={data.me.brContact.fullName}
            salesPartnerId={data.me.brContact.brSalesPerson.id}
          />
        )}
    </>
  );
};

const HeaderWithApollo = (props) => {
  const queryResult = useQuery(currentUserQuery);
  const data = { ...queryResult, ...queryResult.data };

  return <Header {...props} data={data} />;
};

export default HeaderWithApollo;
